import type { BlockPreset } from '@/components/builder/drag-data';
import { newBlock } from '@personalizehq/editor';
// The AUTHORING schema, not the wire one. `materializePreset` returns an `AuthoringBlockNode`, and
// the icon preset carries a managed `{kind:"asset"}` source — which `blockSchema` correctly rejects,
// because that identity is resolved to a URL at compile time and never reaches a bundle (ADR 0013).
// Gating authoring nodes on the wire schema happened to work only while no preset used one.
import { authoringBlockSchema } from '@personalizehq/schemas';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import type { Theme } from '@personalizehq/schemas/ir';

export type InsertTarget = { parentId: string; index: number };

/** Existing container concepts that can structurally wrap a block. Kept beside preset
 * materialization so the palette and context menu create identical publishable IR. */
export const WRAPPER_PRESETS = [
  // Kept BYTE-IDENTICAL to the palette preset (see palette.tsx): the palette and the "Wrap with"
  // menu must materialize the same IR, so a horizontal wrapper fills its width for the same reason a
  // horizontal insert does — a hugging row makes `justify` inert.
  { label: 'Horizontal stack', kind: 'stack', defaults: { direction: 'row', size: { width: 'fill' } } },
  { label: 'Vertical stack', kind: 'stack', defaults: { direction: 'column' } },
  { label: 'Horizontal pages', kind: 'scroll', defaults: { axis: 'horizontal', snap: true } },
  { label: 'Vertical scroll', kind: 'scroll', defaults: { axis: 'vertical' } },
  { label: 'Layer', kind: 'box' },
  { label: 'Grid', kind: 'grid' },
] as const satisfies readonly BlockPreset[];

/** The default edge of an inserted icon, in points. 24 is the size Lucide is drawn at and the size
 *  a glyph beside a line of body text wants; the author resizes from the inspector like any image. */
export const ICON_PT = 24;

/** A colour token to tint a freshly-inserted icon with. Tint is the icon's WHOLE appearance — an
 *  untinted glyph is baked black — so this leans harder than the editor's usual "first token in the
 *  theme": a key that reads like a foreground beats one that reads like a background, and both beat
 *  arbitrary order. Token names are the CUSTOMER's (`text`, `heading-1`, `ink`), never ours, so this
 *  is a preference over their vocabulary and not a lookup that can fail — the first token is always
 *  an acceptable answer, and an empty theme fails loudly at the inspector exactly as it does for
 *  every other token field. */
export function iconTintToken(theme: Theme): string {
  const keys = Object.keys(theme.colors);
  const prefers = (needle: string): string | undefined =>
    keys.find((key) => key.toLowerCase().includes(needle));
  return prefers('text') ?? prefers('foreground') ?? prefers('ink') ?? prefers('primary') ?? keys[0] ?? '';
}

/** THE `icon` COMPOSITION (SPEC §5.12), as authoring defaults: a square, contained, tinted image
 *  pointing at a managed asset. There is no icon block — this function IS the icon. */
export function iconDefaults(assetId: string, theme: Theme): Record<string, unknown> {
  return {
    source: { kind: 'asset', asset_id: assetId },
    aspect_ratio: 1,
    fit: 'contain',
    size: { width: ICON_PT, height: ICON_PT },
    tint: iconTintToken(theme),
  };
}

type TrackableScroll = {
  node: Extract<BlockNode, { block: 'scroll' }>;
  parentId: string;
  index: number;
};

export function trackableScroll(root: BlockNode): TrackableScroll | null {
  const walk = (node: BlockNode): TrackableScroll | null => {
    if (!('children' in node)) return null;
    for (const [index, child] of node.children.entries()) {
      if (child.block === 'scroll' && child.axis === 'horizontal' && child.children.length > 0) {
        return { node: child, parentId: node.id, index };
      }
      const nested = walk(child);
      if (nested) return nested;
    }
    return null;
  };
  return walk(root);
}

/** Turn an authoring concept into a real wire block. Carousel indicator is the one contextual preset:
 * it attaches to the first trackable horizontal-pages block and inserts as its sibling, never inside
 * the scroll (which would make the indicator count itself as a page). The palette disables it until that
 * target exists. */
export function materializePreset(
  preset: BlockPreset,
  theme: Theme,
  root: BlockNode,
  fallback: InsertTarget
): { node: BlockNode; target: InsertTarget } {
  const node = newBlock(preset.kind, theme) as BlockNode;
  if (preset.defaults) Object.assign(node, structuredClone(preset.defaults));

  if (preset.requires !== 'trackable_scroll') return { node: authoringBlockSchema.parse(node), target: fallback };

  const tracked = trackableScroll(root);
  if (!tracked || node.block !== 'progress') {
    throw new Error('Carousel indicator needs horizontal pages with at least one page.');
  }
  node.tracks = tracked.node.id;
  node.segments = tracked.node.children.length;
  return { node: authoringBlockSchema.parse(node), target: { parentId: tracked.parentId, index: tracked.index + 1 } };
}
